<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import SalesReviewPageContent from '../components/SalesReviewPageContent.vue';
import SalesReviewPageHeader from '../components/SalesReviewPageHeader.vue';
import { useSalesReview } from '../composables/useSalesReview';
import {
  salesReviewPartyLocation,
  salesReviewReturnQuery,
} from '../composables/salesReviewNavigation';
import { salesIndexLocation } from '../composables/useSalesRouteState';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const id = computed(() => route.params.salesTransactionId);
const mode = computed(() =>
  route.name === 'operacao_imobiliaria_pos_venda_devolucao'
    ? 'return'
    : 'correction'
);
const review = useSalesReview({ id, mode, t, notify: useAlert });
const showReturnConfirmation = ref(false);
const showResubmitConfirmation = ref(false);

const goBack = () => {
  const name =
    route.name === 'operacao_imobiliaria_pos_venda_devolucao'
      ? 'operacao_imobiliaria_pos_venda'
      : 'operacao_imobiliaria_vendas';
  if (name === 'operacao_imobiliaria_vendas') {
    return router.push(
      salesIndexLocation({
        accountId: route.params.accountId,
        query: route.query,
        fallbackList: 'revisoes',
      })
    );
  }

  return router.push({
    name,
    params: { accountId: route.params.accountId },
  });
};

const openItem = item => {
  const base = {
    accountId: route.params.accountId,
    salesTransactionId: id.value,
  };
  if (item.section === 'imovel') {
    router.push({
      name: 'operacao_imobiliaria_vendas_cadastro_imovel',
      params: base,
      query: salesReviewReturnQuery,
    });
    return;
  }
  if (item.section === 'recibo') {
    router.push({
      name: 'operacao_imobiliaria_vendas_revisar_recibo',
      params: base,
    });
    return;
  }
  const [partyRole] = item.section.split('_');
  router.push({
    ...salesReviewPartyLocation({
      ...base,
      partyRole,
    }),
  });
};

const submit = async () => {
  if (review.isCorrection.value) {
    showResubmitConfirmation.value = true;
    return;
  }

  const saved = await review.submit();
  if (!saved) return;
  showReturnConfirmation.value = true;
};

const closeReturnConfirmation = () => {
  showReturnConfirmation.value = false;
};

const confirmReturn = () => {
  closeReturnConfirmation();
  goBack();
};

const closeResubmitConfirmation = () => {
  showResubmitConfirmation.value = false;
};

const confirmResubmit = async () => {
  const saved = await review.submit();
  if (!saved) return;

  closeResubmitConfirmation();
  goBack();
};
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <SalesReviewPageHeader
      :can-submit="review.canSubmit.value"
      :is-correction="review.isCorrection.value"
      :read-only="!review.canCompose.value"
      :saving="review.saving.value"
      :transaction="review.transaction.value"
      @back="goBack"
      @submit="submit"
    />
    <SalesReviewPageContent
      :can-compose="review.canCompose.value"
      :can-submit="review.canSubmit.value"
      :catalog="review.catalog.value"
      :errors="review.errors.value"
      :is-correction="review.isCorrection.value"
      :items="review.items.value"
      :loading="review.loading.value"
      :participants="review.participants.value"
      :saving="review.saving.value"
      :show-confirmation="showReturnConfirmation"
      :show-resubmit-confirmation="showResubmitConfirmation"
      :transaction="review.transaction.value"
      @close-confirmation="closeReturnConfirmation"
      @close-resubmit-confirmation="closeResubmitConfirmation"
      @confirm-return="confirmReturn"
      @confirm-resubmit="confirmResubmit"
      @open-item="openItem"
      @remove-item="review.removeItem"
      @replace-items="review.replaceItems"
      @submit="submit"
    />
  </div>
</template>
